/**
 * Matchs de coupe (Coupe d'Algérie, play-off LIRF) — règle sur les lignes brutes,
 * importée par les fonctions edge (discipline, calendar-export, PDF des sanctions) et
 * par le client (services/suspensionService, services/matchService,
 * services/deferredCorrectionsService, hooks/useDisciplinaryActions,
 * lib/payments/matchAssignmentPayment).
 * Pur : aucun import, compatible Node et Deno.
 *
 * Un match est de coupe s'il est externe OU si son groupe est de type 'cup' —
 * même définition que la fonction SQL public.is_cup_match (spec cup rounds §6.2).
 * lib/competition/cupRounds.ts n'importe pas ce module : son isCupMatch (objets
 * mappés du client) reprend la même règle, et la copie du renderer PV
 * (pv-renderer/container/src/cup-matches.ts) aussi ; lib/pv/pvCupMatchesMirror.test.ts
 * vérifie que les trois donnent le même verdict — ne modifier l'une qu'avec les autres.
 */

/**
 * Première saison où un match de coupe est placé dans la saison par sa date (la
 * journée de championnat qui le précède) plutôt que par son game_day. Les saisons
 * antérieures (2025-2026) gardent l'ancien comportement.
 */
export const CUP_DATE_PLACEMENT_FROM_SEASON = '2026-2027';

type GroupKindRef = { kind?: string | null };

/**
 * Ligne `matches` brute (PostgREST ou RPC). `league_groups` est l'embed du groupe
 * (objet, ou tableau selon la relation inférée) ; `league_group_kind` la forme à plat
 * renvoyée par certaines RPC.
 */
export function isCupMatchRow(m: {
    is_external_competition?: boolean | null;
    league_groups?: GroupKindRef | GroupKindRef[] | null;
    league_group_kind?: string | null;
}): boolean {
    if (m.is_external_competition) return true;
    if (m.league_group_kind === 'cup') return true;
    const lg = m.league_groups;
    if (Array.isArray(lg)) return lg.some((g) => g?.kind === 'cup');
    return lg?.kind === 'cup';
}

/** Vrai si les matchs de coupe de cette saison se placent par leur date. */
export function cupPlacementByDate(season: string | null | undefined): boolean {
    return !!season && season >= CUP_DATE_PLACEMENT_FROM_SEASON;
}

/**
 * Match de coupe placé dans la saison par sa date (et non par son game_day) :
 * match de coupe d'une saison `cupPlacementByDate`. Faux pour 2025-2026 (figée).
 */
export function isCupPlacedByDate(
    m: Parameters<typeof isCupMatchRow>[0],
    season: string | null | undefined,
): boolean {
    return cupPlacementByDate(season) && isCupMatchRow(m);
}

/** Journée de championnat datée (ligne brute ou Match client). */
export interface DatedGameDay {
    gameDay?: number | null;
    matchDate?: string | null;
    /** Saison du match, quand l'appelant la connaît (client) : la position ne lit que la même saison. */
    season?: string | null;
}

/** Jour calendaire `YYYY-MM-DD` d'une date — même convention que servedMatches.matchDay. */
const dayOf = (value: string | null | undefined): string | null =>
    value ? String(value).slice(0, 10) : null;

/**
 * Position « journée » d'un match de coupe d'après sa date : la plus grande
 * journée des matchs de championnat (non-coupe) joués le même jour calendaire
 * ou avant. Les matchs de coupe passés dans `regular` sont à exclure par
 * l'appelant.
 *  - avant la J1 (aucun match de championnat ce jour-là ou avant) : 0 ;
 *  - même jour qu'un match de championnat : la journée de ce match ;
 *  - date inconnue, ou aucun match de championnat daté avec une journée : null
 *    (le lecteur la traite comme un carton sans match : moitié 1).
 * La plus grande journée (et non celle du dernier match joué) : un match en
 * retard d'une journée ancienne ne ramène pas la coupe en arrière.
 */
export function gameDayPositionFromDate(
    date: string | null | undefined,
    regular: Iterable<DatedGameDay>,
): number | null {
    const day = dayOf(date);
    if (!day) return null;
    let anyDated = false;
    let position = 0;
    for (const r of regular) {
        const gd = Number(r.gameDay);
        const rDay = dayOf(r.matchDate);
        if (!rDay || r.gameDay == null || Number.isNaN(gd)) continue;
        anyDated = true;
        if (rDay <= day && gd > position) position = gd;
    }
    return anyDated ? position : null;
}

/**
 * Journée max des matchs de championnat : `cupByDate(m)` vrai = match de coupe
 * placé par sa date (isCupPlacedByDate / isCupMatch + cupPlacementByDate),
 * ignoré. Sinon même règle qu'avant (`gameDay` vrai et plus grand).
 */
export function regularMaxGameDay<M extends DatedGameDay>(
    matches: Iterable<M>,
    cupByDate: (m: M) => boolean,
): number {
    let max = 0;
    for (const m of matches) {
        if (cupByDate(m)) continue;
        if (m.gameDay && m.gameDay > max) max = m.gameDay;
    }
    return max;
}

/**
 * Journée d'un carton pris dans `match` : son game_day, sauf pour un match de
 * coupe placé par sa date — la journée de championnat qui le précède
 * (gameDayPositionFromDate sur la date du match, à défaut `fallbackDate`, la
 * date de décision). Pas de match : null.
 */
export function sanctionGameDayPosition<M extends DatedGameDay>(
    match: M | null | undefined,
    fallbackDate: string | null | undefined,
    matches: Iterable<M>,
    cupByDate: (m: M) => boolean,
): number | null {
    if (!match) return null;
    if (!cupByDate(match)) return match.gameDay ?? null;
    const regular: M[] = [];
    for (const m of matches) {
        if (cupByDate(m)) continue;
        // Même saison que le match de coupe (quand les deux la portent).
        if (match.season && m.season && m.season !== match.season) continue;
        regular.push(m);
    }
    return gameDayPositionFromDate(match.matchDate || fallbackDate, regular);
}

/**
 * Moitiés de saison des jaunes d'un joueur, au game_day (règle de
 * disciplinary-audit-inconsistencies) :
 *  - carton hors coupe, ou mode 'league' (`groupMidSeasonTs` undefined) :
 *    sanctionGameDayPosition ≤ `midSeasonGameDay` → moitié 1 (null compris) ;
 *  - carton de coupe placé par date en mode 'group' (`groupMidSeasonTs` défini) :
 *    même comparaison que playerSuspension (le cache) — `decision_date` ≤ date de
 *    mi-saison DU GROUPE du joueur → moitié 1 ; mi-saison du groupe pas encore
 *    datée (null) : moitié 1, comme le cache qui ne coupe pas. La journée max de
 *    la ligue n'entre pas en jeu : les groupes n'avancent pas au même rythme.
 */
export function splitYellowsByHalf<
    S extends { matchId?: string | null; decisionDate?: string | null },
    M extends DatedGameDay,
>(
    yellows: S[],
    matchMap: Map<string, M>,
    midSeasonGameDay: number,
    cupByDate: (m: M) => boolean,
    groupMidSeasonTs?: number | null,
): { first: S[]; second: S[] } {
    const first: S[] = [];
    const second: S[] = [];
    for (const s of yellows) {
        const match = s.matchId ? matchMap.get(s.matchId) : undefined;
        let isFirst: boolean;
        if (match && groupMidSeasonTs !== undefined && cupByDate(match)) {
            isFirst = groupMidSeasonTs === null
                || new Date(s.decisionDate as string).getTime() <= groupMidSeasonTs;
        } else {
            const gd = s.matchId ? sanctionGameDayPosition(match, s.decisionDate, matchMap.values(), cupByDate) : null;
            isFirst = gd === null || gd <= midSeasonGameDay;
        }
        (isFirst ? first : second).push(s);
    }
    return { first, second };
}
